import React from 'react';
import { Target, Clock, Calendar, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Боковая панель с финансовыми целями
 */
const FinancialGoalsSidebar = ({ formData, setFormData, results }) => {
  // Если нет активированных целей или нет результатов расчета
  if (!formData.savings.goals.length || !results) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2 text-emerald-500" />
          Финансовые цели
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться ваши финансовые цели и прогресс их достижения.
        </p>
      </div>
    );
  }

  // Получаем данные о целях из результатов расчета
  const { goals, savingsAmount } = results;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Target className="w-5 h-5 mr-2 text-emerald-500" />
          Финансовые цели
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {goals.length} {getGoalCountSuffix(goals.length)}
        </span>
      </div>

      <div className="space-y-4">
        {goals.map((goal) => {
          // Расчет процента выполнения цели
          const progressPercent = Math.min(100, (goal.currentAmount / goal.targetAmount) * 100);
          const timeLeftText = getTimeLeftText(goal);
          
          // Определение статуса цели
          const status = getGoalStatus(goal);
          
          return (
            <div 
              key={goal.id}
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {goal.name}
                </h4>
                <GoalStatusBadge status={status} />
              </div>

              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                <div className="flex items-center">
                  <Calendar className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                  <span>
                    {new Date(goal.deadline).toLocaleDateString('ru-RU', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                  <span>{timeLeftText}</span>
                </div>
              </div>

              {/* Прогресс достижения цели */}
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Прогресс:</span>
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    {progressPercent.toFixed(0)}%
                  </span>
                </div>
                <div className="relative w-full h-2 bg-emerald-200 dark:bg-emerald-800 rounded-full overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 h-full bg-emerald-500 dark:bg-emerald-600"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
              </div>

              {/* Суммы */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                  <div className="text-gray-500 dark:text-gray-400">Накоплено</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(goal.currentAmount)} ₽
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                  <div className="text-gray-500 dark:text-gray-400">Осталось</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(goal.targetAmount - goal.currentAmount)} ₽
                  </div>
                </div>
              </div>

              {/* Информация о ежемесячных сбережениях */}
              {savingsAmount > 0 && (
                <div className="mt-3 flex justify-between items-center text-xs">
                  <span className="text-gray-600 dark:text-gray-400">
                    Ежемесячный взнос:
                  </span>
                  <span className="font-medium">
                    {formatCurrency(savingsAmount / goals.length)} ₽
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Информация о распределении сбережений */}
      {savingsAmount > 0 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <AlertCircle className="w-4 h-4 text-blue-500 dark:text-blue-400 mr-2 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Распределение сбережений
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                При текущем уровне сбережения {formatCurrency(savingsAmount)} ₽/мес и
                {goals.length > 1 
                  ? ` ${goals.length} финансовых целях, `
                  : ' одной финансовой цели, '}
                на каждую цель будет выделено примерно {formatCurrency(savingsAmount / goals.length)} ₽ в месяц.
                Вы можете изменить приоритеты целей для другого распределения.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Компонент бейджа статуса цели
 */
const GoalStatusBadge = ({ status }) => {
  let bgColor, textColor, icon, text;
  
  switch (status) {
    case 'onTrack':
      bgColor = 'bg-emerald-100 dark:bg-emerald-900';
      textColor = 'text-emerald-800 dark:text-emerald-300';
      icon = <CheckCircle className="w-3 h-3 mr-1" />;
      text = 'По плану';
      break;
    case 'delayed':
      bgColor = 'bg-amber-100 dark:bg-amber-900';
      textColor = 'text-amber-800 dark:text-amber-300';
      icon = <Clock className="w-3 h-3 mr-1" />;
      text = 'Задержка';
      break;
    case 'atRisk':
      bgColor = 'bg-red-100 dark:bg-red-900';
      textColor = 'text-red-800 dark:text-red-300';
      icon = <AlertCircle className="w-3 h-3 mr-1" />;
      text = 'Под угрозой';
      break;
    case 'completed':
      bgColor = 'bg-blue-100 dark:bg-blue-900';
      textColor = 'text-blue-800 dark:text-blue-300';
      icon = <CheckCircle className="w-3 h-3 mr-1" />;
      text = 'Достигнута';
      break;
    default:
      bgColor = 'bg-gray-100 dark:bg-gray-900';
      textColor = 'text-gray-800 dark:text-gray-300';
      icon = <Clock className="w-3 h-3 mr-1" />;
      text = 'В процессе';
  }
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}>
      {icon}
      {text}
    </span>
  );
};

/**
 * Определение статуса цели на основе прогресса и сроков
 */
const getGoalStatus = (goal) => {
  // Если цель уже достигнута
  if (goal.currentAmount >= goal.targetAmount) {
    return 'completed';
  }
  
  // Если нет возможности достичь цели при текущих сбережениях
  if (!goal.achievable) {
    return 'atRisk';
  }
  
  const deadline = new Date(goal.deadline);
  const today = new Date();
  
  // Если срок уже прошел
  if (deadline < today) {
    return 'atRisk';
  }
  
  // Если до срока меньше времени, чем требуется для достижения цели при текущих сбережениях
  const monthsToDeadline = (deadline.getFullYear() - today.getFullYear()) * 12 + deadline.getMonth() - today.getMonth();
  
  if (goal.monthsToGoal > monthsToDeadline) {
    return 'delayed';
  }
  
  return 'onTrack';
};

/**
 * Получение текста об оставшемся времени
 */
const getTimeLeftText = (goal) => {
  const deadline = new Date(goal.deadline);
  const today = new Date();
  
  // Если срок уже прошел
  if (deadline < today) {
    return 'Срок прошел';
  }
  
  const diffTime = deadline - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 30) {
    return `${diffDays} ${getDaysSuffix(diffDays)}`;
  }
  
  const diffMonths = (deadline.getFullYear() - today.getFullYear()) * 12 + deadline.getMonth() - today.getMonth();
  
  if (diffMonths < 12) {
    return `${diffMonths} ${getMonthsSuffix(diffMonths)}`;
  }
  
  const years = Math.floor(diffMonths / 12);
  const months = diffMonths % 12;
  
  if (months === 0) {
    return `${years} ${getYearsSuffix(years)}`;
  }
  
  return `${years} ${getYearsSuffix(years)} ${months} ${getMonthsSuffix(months)}`;
};

// Вспомогательные функции для склонения существительных
const getGoalCountSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'целей';
  }
  
  if (lastDigit === 1) {
    return 'цель';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'цели';
  }
  
  return 'целей';
};

const getDaysSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'дней';
  }
  
  if (lastDigit === 1) {
    return 'день';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'дня';
  }
  
  return 'дней';
};

const getMonthsSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'месяцев';
  }
  
  if (lastDigit === 1) {
    return 'месяц';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'месяца';
  }
  
  return 'месяцев';
};

const getYearsSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'лет';
  }
  
  if (lastDigit === 1) {
    return 'год';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'года';
  }
  
  return 'лет';
};

export default FinancialGoalsSidebar;